// API service for connecting to the JPDZ backend
import axios from 'axios';

// Create an axios instance with default config
// Prefer env override; fallback to same-origin proxy path handled by Nginx (production) or localhost (dev)
const resolvedBaseURL =
  (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_API_BASE_URL) ||
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? 'http://localhost:5001/api'
    : '/api');

const api = axios.create({
  baseURL: resolvedBaseURL,
  timeout: 30000, // Increased timeout for backend processing
  headers: {
    'Content-Type': 'application/json',
  },
  // Send cookies for same-site subdomain requests
  withCredentials: true,
});

// Add request interceptor for auth tokens
api.interceptors.request.use(
  (config) => {
    // Try both token storage keys for compatibility
    const rawToken = localStorage.getItem('authToken') || localStorage.getItem('jpdz_token');
    const token = rawToken && rawToken !== 'undefined' && rawToken !== 'null' ? rawToken : ''
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Do not force redirect on 401; allow caller to handle.
    // Avoid clearing cookie-based auth. Only clear obviously bad local tokens.
    if (error.response?.status === 401) {
      const rawToken = localStorage.getItem('authToken') || localStorage.getItem('jpdz_token')
      if (rawToken === 'undefined' || rawToken === 'null' || rawToken === '') {
        localStorage.removeItem('authToken')
        localStorage.removeItem('jpdz_token')
      }
    }
    return Promise.reject(error);
  }
);

// Auth services - Updated to match backend endpoints
export const authService = {
  login: async (username: string, password: string) => {
    const response = await api.post('/auth/login', { username, password });
    const { token, user_id, username: userName, email, full_name } = response.data;
    localStorage.setItem('jpdz_token', token);
    return { 
      id: user_id, 
      username: userName, 
      email, 
      full_name,
      token
    };
  },
  
  signup: async (username: string, email: string, password: string, full_name?: string) => {
    const response = await api.post('/auth/register', { username, email, password, full_name });
    const { token, user_id, username: userName, email: userEmail, full_name: fullName } = response.data;
    localStorage.setItem('jpdz_token', token);
    return { 
      id: user_id, 
      username: userName, 
      email: userEmail, 
      full_name: fullName,
      token
    };
  },
  
  logout: async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
    }
    localStorage.removeItem('jpdz_token');
  },
  
  getCurrentUser: async () => {
    try {
      const response = await api.get('/auth/me');
      return response.data;
    } catch (error) {
      return null;
    }
  },

  verify: async (token?: string) => {
    try {
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await api.get('/auth/verify', { headers });
      return response.data;
    } catch (error) {
      return { success: false, error: 'Token verification failed' };
    }
  },
};

// Chat services - Updated to match backend endpoints
export const chatService = {
  // Send a message using the main chat endpoint
  sendMessage: async (message: string, chatId?: string) => {
    const response = await api.post('/chat', { 
      query: message, 
      chat_id: chatId,
      message: message // Backend accepts both query and message
    });
    return response.data;
  },
  
  // Stream messages using the stream endpoint
  streamMessage: async (
    message: string,
    onChunk: (chunk: any) => void,
    onComplete: () => void,
    chatId?: string
  ) => {
    const response = await fetch(`${api.defaults.baseURL}/chat/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('jpdz_token')}`,
      },
      body: JSON.stringify({ 
        query: message, 
        chat_id: chatId,
        message: message 
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to send message');
    }

    const reader = response.body?.getReader();
    if (!reader) return;

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        onComplete();
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6);
          if (data.trim() === '[DONE]') {
            onComplete();
            return;
          }
          try {
            const parsed = JSON.parse(data);
            onChunk(parsed);
          } catch (e) {
          }
        }
      }
    }
  },
  
  // Get chat history using the backend endpoint
  getChats: async () => {
    const response = await api.get('/user/chats');
    return response.data;
  },
};

// Search services - Updated to use backend endpoints
export const searchService = {
  // Use the main search endpoint
  search: async (query: string) => {
    const response = await api.post('/query', { 
      query,
    });
    return response.data;
  },
  
  // Use JPDZ search for advanced queries
  jpdSearch: async (query: string, options?: { 
    user_context?: any,
    stream?: boolean,
    include_images?: boolean 
  }) => {
    const response = await api.post('/jpdz/search', { 
      query,
      user_context: options?.user_context || {},
      stream: options?.stream ?? false,
      include_images: options?.include_images ?? true
    });
    return response.data;
  },
  
  // Get product details
  getProductDetails: async (brand: string, model: string) => {
    const response = await api.get(`/product/${brand}/${model}`);
    return response.data;
  },
  
  // Get all products
  getProducts: async () => {
    const response = await api.get('/products');
    return response.data;
  },
  
  // Get user's search history
  getUserSearches: async (limit?: number) => {
    const response = await api.get('/user/searches', { 
      params: { limit: limit || 20 } 
    });
    return response.data;
  },
};

// User services
export const userService = {
  getUserInfo: async () => {
    const response = await api.get('/user/info');
    return response.data;
  },
  
  getUserProfile: async () => {
    const response = await api.get('/user/profile');
    return response.data;
  },
  
  updateProfile: async (profileData: any) => {
    const response = await api.post('/update_profile', profileData);
    return response.data;
  },
};

// Health check service
export const healthService = {
  checkHealth: async () => {
    const response = await api.get('/health');
    return response.data;
  },
  
  testModels: async () => {
    const response = await api.get('/test-models');
    return response.data;
  },
};

export default api;